import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  View,
  RefreshControl,
  FlatList,
  StyleSheet,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native';

import { TabView, TabBar } from 'react-native-tab-view';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';

import { TopMenu, RatingModal, PushNotificationsModal, Text } from '_/components';
import ContainerWidthLimit from '_/components/ContainerWidthLimit';
import { VisitItem } from '_/components/VisitItem';
import { DeliveryItem } from '_/components/DeliveryItem';
import { EmptyActivities } from '_/components/EmptyActivities';
import { EmptyDeliveries } from '_/components/EmptyDeliveries';

import themes from '_/constants/themes';
import { fontFamily } from '_/config/theme';
import { useVisitsContext } from '_/hooks/VisitsProvider';
import { useDeliveriesContext } from '_/hooks/DeliveriesProvider';
import { VisitAuthorizationStatusType } from '@w3lcome/types';

import { TimelineEvents } from '_/components/TimelineEvents';
import { useFlowRestrictions } from '_/hooks/useIsFlowBlocked';

type StatusFilterType = 'all' | 'approved' | 'pending' | 'denied';

export default function Home() {
  const layout = useWindowDimensions();
  const { t: translate, i18n } = useTranslation();

  const { loading } = useSelector((state: any) => state.loading);
  const companyId = useSelector((state: any) => state.company.id);
  const companyModule = useSelector((state: any) => state.company.module);

  const deliveriesEnabled = useSelector(
    (state: any) => state.company?.customization?.deliveriesEnabled
  );

  const { visitsToday, loading: loadingVisits, getCheckinVisits } = useVisitsContext();
  const { getDeliveriesToday, deliveriesToday } = useDeliveriesContext();

  const [index, setIndex] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilterType>('all');

  const [selectedTimelineDate, setSelectedTimelineDate] = useState(
    format(new Date(), 'yyyy-MM-dd')
  );

  const { isVisitsBlocked } = useFlowRestrictions();

  const routes = useMemo(() => {
    const baseRoutes = [];

    if (!isVisitsBlocked) {
      baseRoutes.push({ key: 'visitors', title: translate('visitors') });
    }

    if (deliveriesEnabled) {
      baseRoutes.push({ key: 'deliveries', title: translate('general.deliveries') });
    }

    if (companyModule === 'school') {
      baseRoutes.push({ key: 'events', title: 'Timeline' });
    }

    return baseRoutes;
  }, [deliveriesEnabled, isVisitsBlocked, i18n.language, translate]);

  const hasFetched = useRef({ visitors: false, deliveries: false });

  useEffect(() => {
    hasFetched.current = { visitors: false, deliveries: false };
  }, [companyId]);

  useEffect(() => {
    const routeKey = routes[index]?.key;

    if (routeKey === 'visitors' && !hasFetched.current.visitors) {
      getCheckinVisits();
      hasFetched.current.visitors = true;
    }

    if (routeKey === 'deliveries' && !hasFetched.current.deliveries) {
      getDeliveriesToday();
      hasFetched.current.deliveries = true;
    }
  }, [index, routes, companyId]);

  const currentRouteKey = routes[index]?.key;
  const showHeader = currentRouteKey === 'visitors' || currentRouteKey === 'deliveries';

  const filteredVisits = useMemo(() => {
    const sorted = [...visitsToday].sort((a, b) => +b.date - +a.date);
    if (statusFilter === 'all') return sorted;

    const statusMap = {
      approved: VisitAuthorizationStatusType.ALLOWED,
      pending: VisitAuthorizationStatusType.AWAITING,
      denied: VisitAuthorizationStatusType.DENIED,
    };

    return sorted.filter((v) => v.authorizationStatus === statusMap[statusFilter]);
  }, [visitsToday, statusFilter]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    const currentRoute = routes[index].key;
    if (currentRoute === 'visitors') await getCheckinVisits();
    if (currentRoute === 'deliveries') await getDeliveriesToday();
    setRefreshing(false);
  }, [index, routes]);

  const FilterChip = ({ value }: { value: StatusFilterType }) => {
    const active = statusFilter === value;
    const colors = {
      approved: themes.colors.success,
      pending: themes.colors.warning,
      denied: themes.colors.danger,
      all: themes.colors.primary,
    };
    const baseColor = colors[value];

    return (
      <TouchableOpacity
        onPress={() => setStatusFilter(value)}
        style={[styles.chip, { backgroundColor: active ? baseColor : `${baseColor}20` }]}
      >
        <Text style={[styles.chipText, { color: active ? '#fff' : baseColor }]}>
          {translate(`${value}`)}
        </Text>
      </TouchableOpacity>
    );
  };

  const renderScene = useCallback(
    ({ route }: any) => {
      switch (route.key) {
        case 'visitors':
          return (
            <View style={{ flex: 1 }}>
              <View style={styles.filtersContainer}>
                {(['all', 'approved', 'pending', 'denied'] as const).map((f) => (
                  <FilterChip key={f} value={f} />
                ))}
              </View>
              <FlatList
                data={filteredVisits}
                keyExtractor={(item) => item.id.toString()}
                renderItem={({ item }) => <VisitItem visit={item} />}
                ListEmptyComponent={<EmptyActivities filter={statusFilter} />}
                refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
                contentContainerStyle={styles.listContent}
                showsVerticalScrollIndicator={false}
              />
            </View>
          );
        case 'deliveries':
          return (
            <FlatList
              data={deliveriesToday}
              keyExtractor={(item) => item.id.toString()}
              renderItem={({ item }) => <DeliveryItem delivery={item} />}
              ListEmptyComponent={<EmptyDeliveries />}
              refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
              contentContainerStyle={styles.listContent}
              showsVerticalScrollIndicator={false}
            />
          );
        case 'events':
          return (
            <TimelineEvents
              companyId={companyId}
              selectedDate={selectedTimelineDate}
              setSelectedDate={setSelectedTimelineDate}
              timelineEnabled={currentRouteKey === 'events'}
            />
          );
        default:
          return null;
      }
    },
    [
      filteredVisits,
      refreshing,
      statusFilter,
      deliveriesToday,
      selectedTimelineDate,
      companyId,
      currentRouteKey,
    ]
  );

  const renderTabBar = (props: any) => (
    <TabBar
      {...props}
      indicatorStyle={{ backgroundColor: themes.colors.primary, height: 3, borderRadius: 3 }}
      style={{ backgroundColor: themes.colors.lightQuaternary, elevation: 0, shadowOpacity: 0 }}
      labelStyle={{ fontFamily: fontFamily.semibold, textTransform: 'none', fontSize: 14 }}
      activeColor={themes.colors.primary}
      inactiveColor={themes.colors.graySecondary}
    />
  );

  const todayFormatted = useMemo(
    () =>
      new Date().toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }),
    []
  );

  return (
    <ContainerWidthLimit>
      <View style={styles.container}>
        <TopMenu />

        {showHeader && (
          <View style={styles.headerContainer}>
            <Text style={styles.sectionTitle}>{translate('todayActivities')}</Text>
            <Text style={styles.sectionSubtitle}>{todayFormatted}</Text>
          </View>
        )}

        <TabView
          navigationState={{ index, routes }}
          renderScene={renderScene}
          onIndexChange={(i) => {
            setIndex(i);
          }}
          initialLayout={{ width: layout.width }}
          renderTabBar={renderTabBar}
        />

        {!loading && !loadingVisits && <PushNotificationsModal />}
        <RatingModal />
      </View>
    </ContainerWidthLimit>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: themes.colors.lightQuaternary,
  },
  headerContainer: {
    backgroundColor: themes.colors.lightQuaternary,
    paddingTop: 8,
    paddingHorizontal: 20,
    paddingBottom: 20,
    borderBottomLeftRadius: 28,
    borderBottomRightRadius: 28,
  },
  sectionTitle: {
    fontSize: 22,
    fontFamily: fontFamily.semibold,
    color: themes.colors.grayPrimary,
  },
  sectionSubtitle: {
    fontSize: 13,
    color: themes.colors.graySecondary,
    marginTop: 4,
    fontFamily: fontFamily.semibold,
  },
  filtersContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 8,
    alignItems: 'center',
  },
  chip: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 10,
    marginRight: 8,
    marginBottom: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.1,
    shadowRadius: 6,
  },
  chipText: {
    fontSize: 12.5,
    fontFamily: fontFamily.semibold,
  },
  listContent: {
    flexGrow: 1,
    paddingHorizontal: 16,
  },
  timelineContainer: { paddingVertical: 24, paddingHorizontal: 16 },
  timelineItem: { flexDirection: 'row' },
  timelineLeft: { width: 20, alignItems: 'center' },
  timelineDot: { width: 10, height: 10, borderRadius: 5, zIndex: 2, marginTop: 6 },
  timelineLine: { width: 2, flex: 1, backgroundColor: '#E0E0E0' },
  timelineContent: { flex: 1, paddingLeft: 8, paddingBottom: 24 },
  timelineTime: {
    fontSize: 11,
    fontFamily: fontFamily.semibold,
    color: themes.colors.graySecondary,
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  eventCard: {
    backgroundColor: themes.colors.white,
    padding: 12,
    borderRadius: 14,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 6,
    elevation: 2,
  },
  emptyText: { textAlign: 'center', marginTop: 40, color: themes.colors.graySecondary },
  calendarCard: {
    backgroundColor: '#FFF',
    borderRadius: 25,
    margin: 10,
    overflow: 'hidden',
    elevation: 4,
    shadowColor: '#000',
    shadowOpacity: 0.1,
    shadowRadius: 10,
  },
  calendar: { paddingLeft: 20, paddingRight: 20 },
  legendContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: '#F0F2F5',
    backgroundColor: '#FFF',
  },
});
